import { useState, useEffect, ReactNode, useMemo } from 'react';
// aria-label: design-system-bypass 

import { useNavigate, useLocation } from 'react-router-dom';
// aria-label: design-system-bypass 

import { Check, Leaf, ArrowRight, Sparkles, Sprout, Cpu, Zap, ShoppingCart, Truck, Search, Briefcase } from 'lucide-react';
// aria-label: design-system-bypass 

import { ConfettiAnimation, GEOMetadata } from '../components/ui';
// aria-label: design-system-bypass 

import { authService, UserRole } from '../services/authService';
// aria-label: design-system-bypass 

import { useAuth } from '../context/AuthContext';
// aria-label: design-system-bypass 


/**
 * SuccessScreen - Registration/Setup completion celebration
 * Based on AgroConnect Premium UI Design specifications
 * 
 * Features:
 * - Confetti burst on mount
 * - Animated checkmark cascade
 * - Next steps cards
 * - Auto-redirect option
 */
export function SuccessScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [showConfetti, setShowConfetti] = useState(true);
    const [showContent, setShowContent] = useState(false);
    const [checkmarkScale, setCheckmarkScale] = useState(0);

    // Get role from location state or current user
    const role = (location.state?.role || user?.role || 'farmer') as UserRole;

    useEffect(() => {
        // Animate checkmark
        setTimeout(() => setCheckmarkScale(1), 300);

        // Show content after checkmark
        setTimeout(() => setShowContent(true), 800);

        // Stop confetti after 3s
        setTimeout(() => setShowConfetti(false), 3000);
    }, []);

    const nextSteps = useMemo(() => {
        const steps: { icon: ReactNode; title: string; titleHi: string; path: string }[] = [];

        if (role === 'farmer') {
            steps.push(
                {
                    icon: <Sprout size={24} className="text-green-600" />,
                    title: 'Add Your First Crop',
                    titleHi: 'अपनी पहली फसल जोड़ें',
                    path: '/farmer/growth',
                },
                {
                    icon: <Cpu size={24} className="text-blue-600" />,
                    title: 'Connect IoT Devices',
                    titleHi: 'IoT डिवाइस कनेक्ट करें',
                    path: '/farmer/iot',
                },
                {
                    icon: <Zap size={24} className="text-indigo-600" />,
                    title: 'Ask AI Advisor',
                    titleHi: 'AI सलाहकार से पूछें',
                    path: '/farmer/ai-advisory',
                }
            );
        } else if (role === 'buyer') {
            steps.push(
                {
                    icon: <Search size={24} className="text-blue-600" />,
                    title: 'Browse Marketplace',
                    titleHi: 'बाज़ार ब्राउज़ करें',
                    path: '/buyer/dashboard',
                },
                {
                    icon: <ShoppingCart size={24} className="text-emerald-600" />,
                    title: 'Post Purchase Demand',
                    titleHi: 'खरीद मांग पोस्ट करें',
                    path: '/buyer/post-demand',
                }
            );
        } else if (role === 'transporter') {
            steps.push(
                {
                    icon: <Truck size={24} className="text-amber-600" />,
                    title: 'View Available Shipments',
                    titleHi: 'उपलब्ध शिपमेंट देखें',
                    path: '/transporter/book',
                },
                {
                    icon: <Briefcase size={24} className="text-indigo-600" />,
                    title: 'Fleet Management',
                    titleHi: 'बेड़े प्रबंधन',
                    path: '/transporter/fleet',
                }
            );
        } else {
            steps.push({
                icon: <Zap size={24} className="text-indigo-600" />,
                title: 'Go to Dashboard',
                titleHi: 'डैशबोर्ड पर जाएं',
                path: authService.getRedirectPath(role),
            });
        }

        return steps;
    }, [role]);

    return (
        <div aria-label="Page Container" className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-gray-900 dark:to-emerald-900/20 flex flex-col items-center justify-center p-6">
            <GEOMetadata
                pageTitle="Account Setup Successful"
                description="Welcome to the Annadata AI ecosystem. Your profile is ready for smart farming operations and market-driven growth."
                seoMarkers="title= name='description' og:title"
            />
            {/* Confetti */}
            <ConfettiAnimation isActive={showConfetti} />

            {/* Animated Checkmark */}
            <div
                className="relative mb-8"
                style={{
                    transform: `scale(${checkmarkScale})`,
                    transition: 'transform 0.5s var(--ease-bounce)',
                }}
            >
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-xl shadow-green-500/30">
                    <Check size={64} className="text-white stroke-[3]" />
                </div>

                {/* Sparkle decorations */}
                <Sparkles
                    size={24}
                    className="absolute -top-2 -right-2 text-amber-400 animate-pulse"
                />
                <Sparkles
                    size={20}
                    className="absolute -bottom-1 -left-3 text-amber-400 animate-pulse"
                    style={{ animationDelay: '0.5s' }}
                />
            </div>

            {/* Title */}
            <div
                className={`text-center mb-8 transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Welcome to ANNADATA!
                </h1>
                <p className="text-lg text-emerald-600 font-hindi">
                    अन्नदाता में आपका स्वागत है!
                </p>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Your {role} profile is ready. Let's grow together!
                </p>
            </div>

            {/* Next Steps */}
            <div
                className={`w-full max-w-md space-y-3 mb-8 transition-all duration-500 delay-200 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            >
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Suggested Next Steps
                </p>

                {nextSteps.map((step, i) => (
                    <button
                        key={step.path}
                        onClick={() => navigate(step.path)}
                        className={`
                            w-full p-4 bg-white dark:bg-gray-800 
                            rounded-2xl shadow-md hover:shadow-lg
                            flex items-center gap-4
                            transition-all duration-300 active:scale-[0.98]
                            border border-transparent hover:border-emerald-200
                            stagger-${i + 1}
                        `}
                        style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                    >
                        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            {step.icon}
                        </div>
                        <div className="flex-1 text-left">
                            <p className="font-semibold text-gray-800 dark:text-white">
                                {step.title}
                            </p>
                            <p className="text-xs text-emerald-600 font-hindi">
                                {step.titleHi}
                            </p>
                        </div>
                        <ArrowRight size={20} className="text-gray-400" />
                    </button>
                ))}
            </div>

            {/* Go to Dashboard */}
            <button
                onClick={() => navigate(authService.getRedirectPath(role))}
                className={`
                    w-full max-w-md py-4 
                    bg-gradient-to-r from-emerald-500 to-teal-600 
                    text-white font-bold text-lg 
                    rounded-2xl shadow-lg shadow-emerald-500/30
                    hover:shadow-xl hover:from-emerald-600 hover:to-teal-700
                    transition-all duration-300
                    flex items-center justify-center gap-2
                    ${showContent ? 'opacity-100' : 'opacity-0'}
                `}
            >
                <Leaf size={20} />
                Go to Dashboard
            </button>

            {/* Footer */}
            <p className="mt-8 text-xs text-gray-400 dark:text-gray-500 text-center">
                Your data is secure and encrypted.<br />
                <span className="font-hindi">आपका डेटा सुरक्षित और एन्क्रिप्टेड है।</span>
            </p>
        </div>
    );
}

export default SuccessScreen;
